import {
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IStatementBuilderMessage } from 'shared';
import { SparklesIcon } from '@heroicons/react/24/outline';

import { Button } from '../../../../../../common/Atoms/Button';
import { Heading } from '../../../../../../common/Atoms/Typography/Heading';
import { Paragraph } from '../../../../../../common/Atoms/Typography/Paragraph';

import { BuilderChatMessage } from './BuilderChatMessage';

interface BuilderChatProps {
  messages: IStatementBuilderMessage[];
  isSending: boolean;
  onSendMessage: (prompt: string) => Promise<void>;
  onRevertMessage: (message: IStatementBuilderMessage) => Promise<void>;
}

const examplePrompts = [
  `Make the header dark navy`,
  `Move the logo to the left`,
  `Add a thank-you note above the footer`,
];

export function BuilderChat({
  messages,
  isSending,
  onSendMessage,
  onRevertMessage,
}: BuilderChatProps) {
  const [draftPrompt, setDraftPrompt] = useState(``);
  const messageListRef = useRef<HTMLDivElement | null>(null);

  const hasMessages = useMemo(() => messages.length > 0, [messages.length]);

  useEffect(() => {
    if (!messageListRef.current) {
      return;
    }

    messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
  }, [messages, isSending]);

  async function sendCurrentPrompt() {
    const trimmedPrompt = draftPrompt.trim();

    if (!trimmedPrompt || isSending) {
      return;
    }

    setDraftPrompt(``);
    await onSendMessage(trimmedPrompt);
  }

  async function onInputKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === `Enter` && !event.shiftKey) {
      event.preventDefault();
      await sendCurrentPrompt();
    }
  }

  return (
    <div className={ `flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white` }>
      <div className={ `border-b border-slate-200 p-4` }>
        <Heading size={ `lg` }>
          { `AI Statement Builder` }
        </Heading>
        <Paragraph
          size={ `sm` }
          variant={ `secondary` }
          className={ `mt-1` }
        >
          { `Describe how the statement should look and the assistant will update your draft template.` }
        </Paragraph>
      </div>

      <div
        ref={ messageListRef }
        className={ `flex-1 space-y-4 overflow-y-auto bg-slate-50 p-4` }
      >
        {
          !hasMessages &&
          <div className={ `rounded-xl border border-dashed border-slate-300 bg-white p-4` }>
            <div className={ `mb-3 flex items-center gap-2` }>
              <SparklesIcon className={ `h-5 w-5 text-indigo-500` } />
              <Paragraph>
                { `Try a quick prompt to begin` }
              </Paragraph>
            </div>
            <div className={ `flex flex-wrap gap-2` }>
              {
                examplePrompts.map(prompt => (
                  <button
                    key={ prompt }
                    type={ `button` }
                    onClick={ () => setDraftPrompt(prompt) }
                    className={ `rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:border-indigo-300 hover:bg-indigo-50` }
                  >
                    { prompt }
                  </button>
                ))
              }
            </div>
          </div>
        }

        {
          // A running edit shows as the thinking indicator below
          messages.filter(message => message.status !== `running`).map(message => (
            <BuilderChatMessage
              key={ message.id }
              message={ message }
              onRevert={ onRevertMessage }
              disabled={ isSending }
            />
          ))
        }

        {
          isSending &&
          <div className={ `flex justify-start` }>
            <div className={ `max-w-[85%] rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-xs` }>
              <div className={ `flex items-center gap-2` }>
                <span className={ `h-2 w-2 animate-pulse rounded-full bg-slate-400` } />
                <span className={ `h-2 w-2 animate-pulse rounded-full bg-slate-400 [animation-delay:120ms]` } />
                <span className={ `h-2 w-2 animate-pulse rounded-full bg-slate-400 [animation-delay:240ms]` } />
                <Paragraph
                  size={ `xs` }
                  variant={ `secondary` }
                >
                  { `Thinking...` }
                </Paragraph>
              </div>
            </div>
          </div>
        }
      </div>

      <div className={ `border-t border-slate-200 bg-white p-4` }>
        <textarea
          rows={ 4 }
          value={ draftPrompt }
          onChange={ event => setDraftPrompt(event.target.value) }
          onKeyDown={ onInputKeyDown }
          disabled={ isSending }
          placeholder={ `Tell the assistant how you want your statement template changed...` }
          className={ `block w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 shadow-xs focus:border-paidniceGreen-500 focus:outline-hidden focus:ring-2 focus:ring-paidniceGreen-500/20 disabled:cursor-not-allowed disabled:bg-slate-100` }
        />
        <div className={ `mt-3 flex items-center justify-between` }>
          <Paragraph
            size={ `xs` }
            variant={ `secondary` }
          >
            { `Enter to send, Shift+Enter for a newline` }
          </Paragraph>
          <Button
            disabled={ isSending || !draftPrompt.trim() }
            loading={ isSending }
            onClick={ sendCurrentPrompt }
          >
            { `Send` }
          </Button>
        </div>
      </div>
    </div>
  );
}
